import React, { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import {
  BarChart3,
  Clock,
  Layers,
  Lock,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';
import { BUSINESS_TZ, businessDate, businessMonthStart } from '../../utils/businessTime';
import { PageHeader } from '../../components/common/PageHeader';
import {
  ChannelAttribution,
  useLeadAttribution,
} from '../../hooks/useLeadAttribution';
import {
  SectionState,
  useLeadAttributionExtras,
} from '../../hooks/useLeadAttributionExtras';
import { formatCompactCurrency } from '../../utils/formatters';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

// ---------- Formatters ----------

const fmtDollars = (cents: number): string => formatCompactCurrency(cents);

function fmtDollarsFull(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function fmtCpl(cents: number | null): string {
  if (cents === null || cents === 0) return 'n/a';
  return `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

// Commission can legitimately be null (withheld for this role). Never render a
// withheld figure as a zero.
function fmtRevenue(cents: number | null): string {
  if (cents === null) return 'Withheld';
  return fmtDollars(cents);
}

function fmtRoas(bps: number | null): string {
  if (bps === null) return '—';
  const multiple = bps / 10000;
  if (multiple >= 100) return `${multiple.toFixed(0)}x`;
  if (multiple >= 10) return `${multiple.toFixed(0)}x`;
  return `${multiple.toFixed(1)}x`;
}

function fmtRoi(bps: number | null): string {
  if (bps === null) return '—';
  const pct = bps / 100; // bps → pct (bps 500 = 5%)
  if (pct >= 1000) return `${Math.round(pct).toLocaleString()}%`;
  if (pct >= 100) return `${pct.toFixed(0)}%`;
  return `${pct.toFixed(1)}%`;
}

function fmtConversion(converted: number, leads: number): string {
  if (leads === 0) return '—';
  return `${((converted / leads) * 100).toFixed(1)}%`;
}

function channelLabel(c: ChannelAttribution): string {
  if (c.campaignName) return c.campaignName;
  if (c.campaignId) return c.campaignId;
  switch (c.source) {
    case 'meta_ads':
    case 'facebook_lead_ads':
      return 'Facebook / Instagram Ads';
    case 'google_ads':
      return 'Google Ads';
    case 'website_form':
      return 'Website Form';
    case 'manual':
      return 'Manual entry';
    case 'referral':
      return 'Referral';
    default:
      return c.source;
  }
}

function sourceDisplayName(source: string): string {
  switch (source) {
    case 'meta_ads':
    case 'facebook_lead_ads':
      return 'Facebook / Instagram';
    case 'google_ads':
      return 'Google Ads';
    case 'website_form':
      return 'Website Form';
    case 'manual':
      return 'Manual';
    case 'referral':
      return 'Referral Partners';
    case 'csv_import':
      return 'CSV Import';
    default:
      return source;
  }
}

function channelDisplay(c: ChannelAttribution): string {
  switch (c.source) {
    case 'meta_ads':
    case 'facebook_lead_ads':
      return 'Lead Ads';
    case 'google_ads':
      return 'Search Ads';
    case 'website_form':
      return 'Organic SEO';
    case 'referral':
      return 'Partner Network';
    default:
      return c.source;
  }
}

// Conversion rate color per mockup §4 — green ≥20%, amber ≥10%, red <10%
function convPctColorClass(pct: number): string {
  if (pct >= 20) return 'text-emerald-400';
  if (pct >= 10) return 'text-amber-400';
  return 'text-red-400';
}

// Brisbane, not UTC. The callable reads the window on the business clock, so a
// page defaulting to the UTC date asked for a different window than the one it
// displayed for the ten hours a day the two clocks sit on different dates.
function defaultMonthStart(): string {
  return businessMonthStart();
}

function today(): string {
  return businessDate();
}

// A source needs at least this many leads before the page will call it the best
// performer. Without it, one conversion from one lead is a 100% rate and wins.
const MIN_SAMPLE_FOR_BEST = 10;

// Roles the getLeadAttribution callable accepts. Anything else gets a clean
// access-denied state instead of a raw permission error from the transport.
const ATTRIBUTION_ROLES = ['super_admin', 'administrator', 'marketing'];

// ---------- Stat Card (local, matches mockup §4 tile design) ----------

const StatTile: React.FC<{
  title: string;
  value: string;
  subtitle: string;
  accent: string;
}> = ({ title, value, subtitle, accent }) => (
  <div
    className="rounded-lg border border-gray-700 bg-gray-800/40 p-4 border-t-2"
    style={{ borderTopColor: accent }}
  >
    <div className="text-[11px] uppercase tracking-wider text-gray-400">
      {title}
    </div>
    <div
      className="mt-1 text-3xl font-bold"
      style={{ color: accent }}
    >
      {value}
    </div>
    <div className="mt-1 text-[11px] text-gray-500">{subtitle}</div>
  </div>
);

// ---------- Section shell (used by chunk-3 analytics tiles) ----------

const SectionShell: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ icon: Icon, title, subtitle, children, actions }) => (
  <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="flex items-start gap-3 min-w-0">
        <Icon className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          {subtitle && (
            <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex-shrink-0">{actions}</div>}
    </div>
    {children}
  </div>
);

const SectionLoading: React.FC = () => (
  <div className="space-y-2">
    <div className="h-3 rounded bg-gray-700/50 animate-pulse" />
    <div className="h-3 w-2/3 rounded bg-gray-700/50 animate-pulse" />
    <div className="h-3 w-1/2 rounded bg-gray-700/50 animate-pulse" />
  </div>
);

const SectionError: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300">
    {message}
  </div>
);

const SectionAccessDenied: React.FC = () => (
  <div className="flex items-center gap-2 rounded-md border border-gray-700 bg-gray-900/40 p-3 text-xs text-gray-400">
    <Lock className="w-3.5 h-3.5 flex-shrink-0" />
    <span>You don't have access to this report.</span>
  </div>
);

function renderSection<T>(
  state: SectionState<T>,
  renderOk: (data: T) => React.ReactNode
): React.ReactNode {
  if (state.status === 'loading' || state.status === 'idle') {
    return <SectionLoading />;
  }
  if (state.status === 'access-denied') {
    return <SectionAccessDenied />;
  }
  if (state.status === 'error' || !state.data) {
    return <SectionError message={state.error ?? 'Failed to load section.'} />;
  }
  return renderOk(state.data);
}

// AUD currency, integer dollars (per playbook §10).
function fmtAud(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  });
}

// Convert ROAS basis points → "1.4x" style multiple.
function fmtRoasMultiple(bps: number): string {
  if (!Number.isFinite(bps) || bps === 0) return '—';
  const multiple = bps / 10000;
  if (multiple >= 100) return `${multiple.toFixed(0)}x`;
  if (multiple >= 10) return `${multiple.toFixed(1)}x`;
  return `${multiple.toFixed(2)}x`;
}

// ---------- Main page ----------

const LeadAttributionPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  const role = userData?.role;

  const [windowStart, setWindowStart] = useState<string>(defaultMonthStart());
  const [windowEnd, setWindowEnd] = useState<string>(today());

  const { data, loading, error, refresh } = useLeadAttribution({
    companyId,
    windowStart,
    windowEnd,
  });

  // PR-3 chunk-3 analytics — independent fetch + per-section state.
  const extras = useLeadAttributionExtras({ companyId });

  const totals = data?.totals;
  const channels = data?.channels ?? [];
  const distribution = data?.sourceDistribution ?? [];
  const conversionRates = data?.conversionRates ?? [];

  // Commission is withheld for the marketing role rather than zeroed. The tile
  // is hidden outright: a redacted figure has no honest number to show.
  const revenueRedacted = data?.revenueRedacted === true;

  // Best converting source, with a minimum sample. The old tile called itself
  // "Best Channel ROI", showed a conversion rate rather than any return figure,
  // and let one conversion from one lead take the crown.
  const bestConverting = useMemo(
    () => conversionRates.find((r) => r.leads >= MIN_SAMPLE_FOR_BEST) ?? null,
    [conversionRates]
  );

  const hasAnyActivity = useMemo(
    () =>
      (totals?.leads ?? 0) > 0 ||
      (totals?.spendCents ?? 0) > 0 ||
      (totals?.converted ?? 0) > 0,
    [totals]
  );

  // Build Recharts-friendly payload for the donut. Empty-array guard so the
  // chart renders a gray placeholder circle instead of erroring.
  const donutData = useMemo(() => {
    if (distribution.length === 0) {
      return [{ name: 'No data', value: 1, colorHex: '#374151' }];
    }
    return distribution.map((d) => ({
      name: sourceDisplayName(d.source),
      value: d.leads,
      colorHex: d.colorHex,
      percentage: d.percentage,
    }));
  }, [distribution]);

  // Roles the callable rejects get a clean state rather than a raw transport
  // error. Nav and route guards belong to another surface; this is only about
  // what this page renders when it is reached.
  if (role && !ATTRIBUTION_ROLES.includes(role)) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <Lock className="w-8 h-8 text-gray-500 mx-auto mb-3" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-white mb-2">
            You do not have access to Lead Attribution
          </h2>
          <p className="text-xs text-gray-400">
            This report is available to super admins, administrators and the marketing
            role. Ask an administrator if you need it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        icon={BarChart3}
        title="Lead Attribution"
        subtitle="See which channels produce settled deals — from first touch to commission earned"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      {/* Date window */}
      <div className="flex items-center gap-3 text-sm">
        <label className="text-gray-400">From</label>
        <input
          type="date"
          value={windowStart}
          onChange={(e) => setWindowStart(e.target.value)}
          className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white"
        />
        <label className="text-gray-400">to</label>
        <input
          type="date"
          value={windowEnd}
          onChange={(e) => setWindowEnd(e.target.value)}
          className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white"
        />
        <span className="text-[11px] text-gray-500">
          {data?.timezone ?? BUSINESS_TZ} dates
        </span>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Primary stat tiles. Each title says what it actually counts. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile
          title="Total Leads"
          value={(totals?.leads ?? 0).toLocaleString()}
          subtitle={`across ${distribution.length || 0} channel${distribution.length === 1 ? '' : 's'}`}
          accent="#3b82f6"
        />
        {/* Was "Settled from Leads". It counts leads whose CRM status is
            'converted', which is not the same thing as a settled loan. */}
        <StatTile
          title="Leads Marked Converted"
          value={(totals?.converted ?? 0).toLocaleString()}
          subtitle={`${fmtConversion(totals?.converted ?? 0, totals?.leads ?? 0)} of captured leads, CRM status`}
          accent="#22c55e"
        />
        {/* Hidden rather than zeroed when commission is withheld for this role. */}
        {!revenueRedacted && (
          <StatTile
            title="Commission on Linked Loans"
            value={fmtRevenue(totals?.settledRevenueCents ?? null)}
            subtitle={
              (totals?.settledRevenueCents ?? 0) > 0
                ? 'all commission to date, counted once per loan'
                : 'no commission recorded on these loans yet'
            }
            accent="#f59e0b"
          />
        )}
        {/* Was "Best Channel ROI", which showed a conversion rate and let one
            conversion from one lead win. Named for what it measures, and it
            refuses to crown a source below the minimum sample. */}
        <StatTile
          title="Best Converting Source"
          value={bestConverting ? sourceDisplayName(bestConverting.source) : 'n/a'}
          subtitle={
            bestConverting
              ? `${bestConverting.conversionPct.toFixed(1)}% of ${bestConverting.leads.toLocaleString()} leads`
              : `needs a source with at least ${MIN_SAMPLE_FOR_BEST} leads`
          }
          accent="#9333ea"
        />
      </div>

      {/* The commission basis travels in the payload and is rendered, because a
          date picker sitting above a lifetime-to-date figure invites exactly
          the wrong reading. */}
      {!revenueRedacted && data?.commissionBasisNote && (
        <p className="text-[11px] text-gray-500">{data.commissionBasisNote}</p>
      )}

      {/* Empty state */}
      {!loading && !hasAnyActivity && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-8 text-center">
          <Sparkles className="w-8 h-8 text-gray-500 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-white mb-2">
            No attribution data in this window
          </h3>
          <p className="text-xs text-gray-400 max-w-md mx-auto">
            Once leads flow in via the webhook and your Meta / Google Ads
            connections are authorised, spend and conversion rates will
            appear here. Complete Step 5 of the{' '}
            <a
              href="/leads/setup"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              Setup Wizard
            </a>{' '}
            to connect ad accounts.
          </p>
        </div>
      )}

      {/* Donut + Conversion Bars (mockup §4 two-column split) */}
      {hasAnyActivity && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Donut */}
          <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
            <h3 className="text-sm font-semibold text-white mb-4">
              Lead Source Distribution
            </h3>
            <div className="flex items-center gap-6">
              <div className="flex-shrink-0 relative" style={{ width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPieChart>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      dataKey="value"
                      stroke="none"
                    >
                      {donutData.map((entry, idx) => (
                        <Cell key={idx} fill={entry.colorHex} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: '#111827',
                        border: '1px solid #374151',
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                      formatter={(value: number | string, name: string) => [
                        `${value} leads`,
                        name,
                      ]}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="text-2xl font-bold text-white">
                    {(totals?.leads ?? 0).toLocaleString()}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-400">
                    Total Leads
                  </div>
                </div>
              </div>
              <div className="flex-1 space-y-2 text-xs">
                {distribution.length === 0 ? (
                  <div className="text-gray-500">
                    No sources in this window yet.
                  </div>
                ) : (
                  distribution.map((d) => (
                    <div
                      key={d.source}
                      className="flex items-center justify-between gap-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                          style={{ background: d.colorHex }}
                        />
                        <span className="text-gray-300 truncate">
                          {sourceDisplayName(d.source)}
                        </span>
                      </div>
                      <span className="font-mono flex-shrink-0 text-right">
                        <span className="text-gray-200">
                          {d.leads.toLocaleString()}
                        </span>
                        <span className="text-gray-500">
                          {' '}
                          · {d.percentage.toFixed(1)}%
                        </span>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Horizontal conversion-rate bars */}
          <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
            <h3 className="text-sm font-semibold text-white mb-4">
              Source to Settlement Rate
            </h3>
            {conversionRates.length === 0 ? (
              <div className="text-xs text-gray-500">
                No conversion data in this window yet.
              </div>
            ) : (
              <div className="space-y-3">
                {conversionRates.map((row) => {
                  // Width percent: scale the longest bar to 100%, others
                  // relative. Matches the mockup's visual proportions and
                  // keeps very-low-rate bars visible.
                  const maxPct = Math.max(
                    ...conversionRates.map((r) => r.conversionPct)
                  );
                  const widthPct =
                    maxPct > 0
                      ? Math.max(4, (row.conversionPct / maxPct) * 100)
                      : 4;
                  return (
                    <div key={row.source} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-300">
                          {sourceDisplayName(row.source)}
                        </span>
                        <span
                          className={`font-mono font-semibold ${convPctColorClass(row.conversionPct)}`}
                        >
                          {row.conversionPct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-gray-900 overflow-hidden">
                        <div
                          className="h-full transition-all duration-500"
                          style={{
                            width: `${widthPct}%`,
                            background: row.colorHex,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Full Attribution Table (mockup §4 — Lead to Commission) */}
      {hasAnyActivity && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-white">
              Full Attribution — Lead to Commission
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900/50 text-gray-400 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Source</th>
                  <th className="px-4 py-3 text-left font-medium">Channel</th>
                  <th className="px-4 py-3 text-right font-medium">Leads</th>
                  <th className="px-4 py-3 text-right font-medium">Settled</th>
                  <th className="px-4 py-3 text-right font-medium">Conv %</th>
                  {/* One commission column, not two. The previous table had a
                      "Settled Value" and a "Commission" column both rendering
                      settledRevenueCents, the same number formatted twice. */}
                  {!revenueRedacted && (
                    <th className="px-4 py-3 text-right font-medium">
                      Commission
                    </th>
                  )}
                  <th className="px-4 py-3 text-right font-medium">CPL</th>
                  {!revenueRedacted && (
                    <th className="px-4 py-3 text-right font-medium">ROI</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {channels.map((c, idx) => {
                  const convPct =
                    c.leads > 0 ? (c.converted / c.leads) * 100 : 0;
                  return (
                    <tr
                      key={`${c.source}-${c.campaignId ?? 'none'}-${idx}`}
                      className="text-gray-200"
                    >
                      <td className="px-4 py-3 font-medium text-white">
                        {channelLabel(c)}
                      </td>
                      <td className="px-4 py-3 text-gray-400">
                        {channelDisplay(c)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {c.leads.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {c.converted.toLocaleString()}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-mono font-semibold ${convPctColorClass(convPct)}`}
                      >
                        {c.leads > 0 ? `${convPct.toFixed(1)}%` : '—'}
                      </td>
                      {!revenueRedacted && (
                        <td className="px-4 py-3 text-right font-mono text-emerald-400 font-semibold">
                          {c.settledRevenueCents === null
                            ? 'Withheld'
                            : fmtDollarsFull(c.settledRevenueCents)}
                        </td>
                      )}
                      <td className="px-4 py-3 text-right font-mono">
                        {fmtCpl(c.cplCents)}
                      </td>
                      {!revenueRedacted && (
                        <td className="px-4 py-3 text-right font-mono font-bold text-emerald-400">
                          {fmtRoi(c.roasBps)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Supplementary aggregate tiles retained for ad-spend-focused users */}
      {hasAnyActivity && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile
            title="Total Spend"
            value={fmtDollarsFull(totals?.spendCents ?? 0)}
            subtitle="across all channels"
            accent="#2563eb"
          />
          <StatTile
            title="Cost per Lead"
            value={fmtCpl(totals?.cplCents ?? null)}
            subtitle="blended across channels"
            accent="#f59e0b"
          />
          <StatTile
            title="Clicks"
            value={(totals?.clicks ?? 0).toLocaleString()}
            subtitle={`${(totals?.impressions ?? 0).toLocaleString()} impressions`}
            accent="#06b6d4"
          />
          {!revenueRedacted && (
            <StatTile
              title="ROAS"
              value={fmtRoas(totals?.roasBps ?? null)}
              subtitle="commission divided by spend"
              accent="#ef4444"
            />
          )}
        </div>
      )}

      {/* ====================================================================
          PR-3 chunk 3 — Lead Funnel Analytics
          Each section renders independently. Auth/permission failures show as
          access-denied without breaking neighbours.
          ==================================================================== */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* (1) Conversion by Source */}
        <SectionShell
          icon={Target}
          title="Conversion by Source"
          subtitle="Last 90 days — leads vs settled"
        >
          {renderSection(extras.conversion, (d) => {
            if (d.bySource.length === 0) {
              return (
                <div className="text-xs text-gray-500">
                  No leads recorded in the last 90 days.
                </div>
              );
            }
            const maxLeads = Math.max(...d.bySource.map((r) => r.leadsCount), 1);
            return (
              <div className="space-y-3">
                <div className="flex items-baseline gap-4 text-xs text-gray-400 mb-2">
                  <span>
                    Total leads:{' '}
                    <span className="text-white font-mono font-semibold">
                      {d.totalLeads.toLocaleString()}
                    </span>
                  </span>
                  <span>
                    Settled:{' '}
                    <span className="text-emerald-400 font-mono font-semibold">
                      {d.totalConverted.toLocaleString()}
                    </span>
                  </span>
                </div>
                {d.bySource.map((row) => {
                  const leadsPct = (row.leadsCount / maxLeads) * 100;
                  const convPct =
                    row.leadsCount > 0
                      ? (row.convertedCount / maxLeads) * 100
                      : 0;
                  return (
                    <div
                      key={row.source}
                      className="space-y-1"
                      title={`${row.conversionPct.toFixed(1)}% conversion (${row.convertedCount} of ${row.leadsCount})`}
                    >
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-300 truncate">
                          {sourceDisplayName(row.source)}
                        </span>
                        <span className="font-mono text-gray-400">
                          <span className="text-emerald-400 font-semibold">
                            {row.convertedCount.toLocaleString()}
                          </span>
                          {' / '}
                          {row.leadsCount.toLocaleString()}
                          {'  '}
                          <span className={convPctColorClass(row.conversionPct)}>
                            ({row.conversionPct.toFixed(1)}%)
                          </span>
                        </span>
                      </div>
                      <div className="relative h-3 rounded-md bg-gray-900 overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-blue-500/25"
                          style={{ width: `${Math.max(2, leadsPct)}%` }}
                        />
                        <div
                          className="absolute inset-y-0 left-0 bg-emerald-500"
                          style={{ width: `${Math.max(0, convPct)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </SectionShell>

        {/* (4) Multi-Service tile */}
        <SectionShell
          icon={Layers}
          title="Multi-Service Clients"
          subtitle="Last 365 days: share of clients using more than one VALUTTEN service"
        >
          {renderSection(extras.multiService, (d) => {
            if (d.clientsTotal === 0) {
              return (
                <div className="text-xs text-gray-500">
                  No clients on record yet.
                </div>
              );
            }
            return (
              <div className="space-y-4">
                <div>
                  <div className="text-4xl font-bold text-white">
                    {d.multiServicePct.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    of clients use{' '}
                    <span className="text-white font-semibold">
                      multiple services
                    </span>{' '}
                    ({d.clientsMultiService.toLocaleString()} of{' '}
                    {d.clientsTotal.toLocaleString()})
                  </div>
                </div>
                <div className="space-y-2">
                  {d.byServiceCount.length === 0 ? (
                    <div className="text-[11px] text-gray-500">
                      No service-count breakdown available.
                    </div>
                  ) : (
                    d.byServiceCount.map((row) => {
                      const label =
                        row.serviceCount >= 3
                          ? '3+ services'
                          : row.serviceCount === 1
                            ? '1 service'
                            : `${row.serviceCount} services`;
                      const pct =
                        d.clientsTotal > 0
                          ? (row.clientCount / d.clientsTotal) * 100
                          : 0;
                      return (
                        <div key={row.serviceCount} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-300">{label}</span>
                            <span className="font-mono text-gray-400">
                              {row.clientCount.toLocaleString()}{' '}
                              <span className="text-gray-500">
                                ({pct.toFixed(1)}%)
                              </span>
                            </span>
                          </div>
                          <div className="h-1.5 rounded-full bg-gray-900 overflow-hidden">
                            <div
                              className="h-full bg-purple-500"
                              style={{ width: `${Math.max(2, pct)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </SectionShell>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* (2) Time-to-Close histogram */}
        <SectionShell
          icon={Clock}
          title="Time to Close"
          subtitle="Last 365 days — settlement age from lead creation"
        >
          {renderSection(extras.timeToClose, (d) => {
            const totalLeads = d.buckets.reduce(
              (sum, b) => sum + b.leadsCount,
              0
            );
            if (totalLeads === 0) {
              return (
                <div className="text-xs text-gray-500">
                  No closed leads recorded in this window.
                </div>
              );
            }
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      Median
                    </div>
                    <div className="text-2xl font-bold text-white mt-1">
                      {d.medianDays.toLocaleString()}
                      <span className="text-sm text-gray-400 font-normal ml-1">
                        days
                      </span>
                    </div>
                  </div>
                  <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      P90
                    </div>
                    <div className="text-2xl font-bold text-white mt-1">
                      {d.p90Days.toLocaleString()}
                      <span className="text-sm text-gray-400 font-normal ml-1">
                        days
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={d.buckets}
                      margin={{ top: 8, right: 8, bottom: 0, left: -16 }}
                    >
                      <CartesianGrid stroke="#374151" strokeDasharray="3 3" />
                      <XAxis
                        dataKey="label"
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        stroke="#374151"
                      />
                      <YAxis
                        tick={{ fill: '#9ca3af', fontSize: 11 }}
                        stroke="#374151"
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          background: '#111827',
                          border: '1px solid #374151',
                          borderRadius: 6,
                          fontSize: 12,
                        }}
                        formatter={(value: number | string) => [
                          `${value} leads`,
                          'Closed',
                        ]}
                      />
                      <Bar
                        dataKey="leadsCount"
                        fill="#3b82f6"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })}
        </SectionShell>

        {/* (3) ROI tile + per-source ROAS */}
        <SectionShell
          icon={TrendingUp}
          title="Return on Ad Spend"
          subtitle="Last 90 days — spend, revenue, ROAS by source"
        >
          {renderSection(extras.roi, (d) => {
            const hasSpend = d.totalSpendCents > 0 || d.bySource.length > 0;
            if (!hasSpend) {
              return (
                <div className="rounded-md border border-gray-700 bg-gray-900/40 p-4 text-center">
                  <Sparkles className="w-6 h-6 text-gray-500 mx-auto mb-2" />
                  <div className="text-sm font-semibold text-white mb-1">
                    Connect Google Ads to see ROI
                  </div>
                  <p className="text-xs text-gray-400 mb-3 max-w-xs mx-auto">
                    Authorise your ad accounts to track cost per lead and
                    return on ad spend per source.
                  </p>
                  <Link
                    to="/leads/integrations"
                    className="inline-block text-xs text-blue-400 hover:text-blue-300 underline"
                  >
                    Open Integrations
                  </Link>
                </div>
              );
            }
            return (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      Spend
                    </div>
                    <div className="text-lg font-bold text-white mt-1">
                      {fmtAud(d.totalSpendCents)}
                    </div>
                  </div>
                  <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      Revenue
                    </div>
                    <div className="text-lg font-bold text-emerald-400 mt-1">
                      {fmtAud(d.totalRevenueCents)}
                    </div>
                  </div>
                  <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">
                      ROAS
                    </div>
                    <div className="text-lg font-bold text-white mt-1">
                      {fmtRoasMultiple(d.totalRoas)}
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {d.bySource.length === 0 ? (
                    <div className="text-[11px] text-gray-500">
                      No per-source breakdown yet.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-12 gap-2 px-2 text-[10px] uppercase tracking-wider text-gray-500">
                        <div className="col-span-4">Source</div>
                        <div className="col-span-3 text-right">Spend</div>
                        <div className="col-span-3 text-right">Revenue</div>
                        <div className="col-span-2 text-right">ROAS</div>
                      </div>
                      {d.bySource.map((row) => (
                        <div
                          key={row.source}
                          className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 rounded-md hover:bg-gray-900/40 text-xs"
                        >
                          <div className="col-span-4 text-gray-200 truncate">
                            {sourceDisplayName(row.source)}
                          </div>
                          <div className="col-span-3 text-right font-mono text-gray-300">
                            {fmtAud(row.spendCents)}
                          </div>
                          <div className="col-span-3 text-right font-mono text-emerald-400">
                            {fmtAud(row.revenueCents)}
                          </div>
                          <div className="col-span-2 text-right font-mono font-semibold text-white">
                            {fmtRoasMultiple(row.roas)}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </SectionShell>
      </div>
    </div>
  );
};

export default LeadAttributionPage;
